import type Player from 'video.js/dist/types/player';
import logger from '~/services/logger';
import { BrightcoveDataCollector } from './brightcove-data-collector';
import type { BrightcoveAnalyticsPlugin } from './types';

function brightcoveAnalyticsPlugin(
  this: Player,
  options: BrightcoveAnalyticsPlugin
) {
  if (!options.config?.accountId) {
    logger.error('BrightcoveAnalyticsPlugin: accountId is required');
    return;
  }

  let dataCollector: BrightcoveDataCollector;
  let playRequestStartTime: number;
  let loadStartTime: number;
  let rebufferingStartTime: number | null = null;

  // Extract brightcoveVideoId from playableItem if available
  const brightcoveVideoId =
    options.playableItem?.extensions?.brightcove?.video_id;

  const videoData = {
    id:
      brightcoveVideoId ||
      options.playableItem?.id ||
      options.trackData?.media_id ||
      'unknown',
    name:
      options.playableItem?.title || options.trackData?.name || 'Unknown Video',
    duration: 0,
  };

  // Initialize the data collector
  const initializeCollector = () => {
    dataCollector = new BrightcoveDataCollector({
      ...options.config,
      sessionId: options.config.sessionId || generateSessionId(),
    });

    // Track player_load event as soon as the collector is initialized
    dataCollector.trackPlayerLoad();
  };

  const generateSessionId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Event handlers
  const onLoadStart = () => {
    loadStartTime = Date.now();

    // Initialize collector if not already done
    if (!dataCollector) {
      initializeCollector();
    }
  };

  const onLoadedMetadata = async () => {
    const duration = this.duration();
    if (duration && !isNaN(duration)) {
      videoData.duration = Math.round(duration);
    }

    // Track video_impression event when metadata is loaded
    if (dataCollector) {
      await dataCollector.trackVideoImpression(videoData);
    }
  };

  const onPlay = async () => {
    if (!dataCollector) {
      logger.error(
        'BrightcoveAnalyticsPlugin: DataCollector not initialized during play'
      );
      return;
    }

    playRequestStartTime = Date.now();

    // Track play request (Player V7 sends this once per session per media stream)
    await dataCollector.trackPlayRequest(videoData);
  };

  const onPlaying = async () => {
    if (!dataCollector) return;

    const now = Date.now();
    const loadTimeMs = loadStartTime ? now - loadStartTime : undefined;
    const startTimeMs = playRequestStartTime
      ? now - playRequestStartTime
      : undefined;

    // Track video view (first frame rendered)
    await dataCollector.trackVideoView(videoData, loadTimeMs, startTimeMs);

    // Start engagement tracking
    dataCollector.startEngagementTracking(
      () => this.currentTime() || 0,
      videoData
    );
  };

  const onTimeUpdate = () => {
    // Update video data with current duration if it changed (for live streams)
    const duration = this.duration();
    if (duration && !isNaN(duration) && duration !== videoData.duration) {
      videoData.duration = Math.round(duration);
    }
  };

  const onPause = () => {
    if (dataCollector) {
      dataCollector.stopEngagementTracking();
    }
  };

  const onSeeking = () => {
    if (dataCollector) {
      dataCollector.stopEngagementTracking();
    }
  };

  const onSeeked = () => {
    if (dataCollector) {
      dataCollector.startEngagementTracking(
        () => this.currentTime() || 0,
        videoData
      );
    }
  };

  const onWaiting = () => {
    rebufferingStartTime = Date.now();
    if (dataCollector) {
      dataCollector.updateQoEData(undefined, 1); // Increment rebuffering count
    }
  };

  const onCanPlay = () => {
    if (rebufferingStartTime) {
      const rebufferingDuration = (Date.now() - rebufferingStartTime) / 1000;

      if (dataCollector) {
        dataCollector.updateQoEData(rebufferingDuration);
      }

      rebufferingStartTime = null;
    }
  };

  const onError = async () => {
    if (!dataCollector) {
      initializeCollector();
    }

    const error = this.error();
    const errorCode = error ? `${error.code}` : 'unknown_error';

    await dataCollector.trackError(errorCode, videoData);

    logger.error(`BrightcoveAnalyticsPlugin: Error tracked - ${errorCode}`);
  };

  const onAdStart = async () => {
    if (dataCollector) {
      await dataCollector.trackAdModeBegin();
      dataCollector.stopEngagementTracking();
    }
  };

  const onAdEnd = async () => {
    if (dataCollector) {
      await dataCollector.trackAdModeComplete();
      dataCollector.startEngagementTracking(
        () => this.currentTime() || 0,
        videoData
      );
    }
  };

  const onEnded = () => {
    if (dataCollector) {
      dataCollector.stopEngagementTracking();
    }
  };

  const onDispose = () => {
    if (dataCollector) {
      dataCollector.dispose();
    }
    removeListeners();
  };

  const addListeners = () => {
    this.on('loadstart', onLoadStart);
    this.on('loadedmetadata', onLoadedMetadata);
    this.on('play', onPlay);
    this.on('playing', onPlaying);
    this.on('timeupdate', onTimeUpdate);
    this.on('pause', onPause);
    this.on('seeking', onSeeking);
    this.on('seeked', onSeeked);
    this.on('waiting', onWaiting);
    this.on('canplay', onCanPlay);
    this.on('error', onError);
    this.on('ended', onEnded);
    this.on('dispose', onDispose);

    // Ad events (if IMA plugin is present)
    this.on('adstart', onAdStart);
    this.on('adend', onAdEnd);
  };

  const removeListeners = () => {
    this.off('loadstart', onLoadStart);
    this.off('loadedmetadata', onLoadedMetadata);
    this.off('play', onPlay);
    this.off('playing', onPlaying);
    this.off('timeupdate', onTimeUpdate);
    this.off('pause', onPause);
    this.off('seeking', onSeeking);
    this.off('seeked', onSeeked);
    this.off('waiting', onWaiting);
    this.off('canplay', onCanPlay);
    this.off('error', onError);
    this.off('ended', onEnded);
    this.off('dispose', onDispose);
    this.off('adstart', onAdStart);
    this.off('adend', onAdEnd);
  };

  // Initialize event listeners
  addListeners();
}

export default brightcoveAnalyticsPlugin;
